import UserActionCard from '../UserActionCard';
import UserActionsEmptyState from '../UserActionsEmptyState';
import { formatPluralCount } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { UserActionResponse } from '../../api';
import { UserActionsGroup, UserActionsSectionProps } from './types';

const getUserActionsGroupKey = (group: UserActionsGroup): string => {
  return group.id;
};

const getUserActionGroupKey = (item: UserActionResponse): string => {
  if (
    item.code === 'DECIDE_UNRELEASED_ITEM' &&
    item.details &&
    'shipment_id' in item.details &&
    item.details.shipment_id
  ) {
    return `SHIPMENT:${item.details.shipment_id}`;
  }

  return `${item.subject.type}:${item.subject.id}`;
};

const collapseUserActions = (
  actions: UserActionResponse[]
): { displayActions: UserActionResponse[]; taskCount: number } => {
  const unreleasedActions = actions.filter((action) => action.code === 'DECIDE_UNRELEASED_ITEM');
  let hasAppendedUnreleased = false;
  const displayActions: UserActionResponse[] = [];

  for (const action of actions) {
    if (action.code === 'DECIDE_UNRELEASED_ITEM') {
      if (!hasAppendedUnreleased) {
        hasAppendedUnreleased = true;
        const details = action.details && 'type' in action.details && action.details.type === 'ITEM'
          ? {
              ...action.details,
              customs_status: action.details.customs_status ?? null,
              item_count: Math.max(unreleasedActions.length, action.details.item_count ?? 1)
            }
          : action.details;

        displayActions.push({
          ...action,
          details
        });
      }
    } else {
      displayActions.push(action);
    }
  }

  return {
    displayActions,
    taskCount: displayActions.length
  };
};

const groupUserActions = (items: UserActionsSectionProps['items']): UserActionsGroup[] => {
  const groups = new Map<string, UserActionsGroup>();

  items.forEach((item) => {
    const groupKey = getUserActionGroupKey(item);
    const currentGroup = groups.get(groupKey);

    if (currentGroup) {
      currentGroup.actions.push(item);
      return;
    }

    groups.set(groupKey, {
      actions: [item],
      id: groupKey
    });
  });

  return Array.from(groups.values());
};

const UserActionsSection = (props: UserActionsSectionProps) => {
  const {
    isError,
    items,
    listClassName,
    onAutoConfirmExpired,
    onClick,
    shipmentAutoConfirmAt,
    title = 'Требуют действия',
    variant = 'main'
  } = props;

  const groupedItems = groupUserActions(items);
  const isMainVariant = variant === 'main';
  const shipmentItems = isMainVariant ? [] : collapseUserActions(items).displayActions;

  return (
    <section className="flex w-full flex-col gap-4">
      <h2 className="font-sans text-lg font-semibold leading-6 text-foreground">{title}</h2>
      {items.length === 0 ? (
        <UserActionsEmptyState
          isError={isError}
          variant={variant}
        />
      ) : (
        <div className={cn('flex w-full flex-col gap-3', listClassName)}>
          {isMainVariant ? (
            groupedItems.map((group) => {
              const { displayActions, taskCount } = collapseUserActions(group.actions);
              const firstAction = group.actions[0];
              const shipmentNumber = group.actions.find((action) => action.details?.shipment_number)?.details?.shipment_number;

              return (
                <UserActionCard
                  actions={displayActions}
                  invoiceNumber={firstAction?.details?.invoice_number}
                  key={getUserActionsGroupKey(group)}
                  onClick={onClick}
                  shipmentNumber={shipmentNumber}
                  supplierName={firstAction?.details?.supplier_name}
                  taskCount={formatPluralCount({
                    value: taskCount,
                    variant: 'task'
                  })}
                  variant="main"
                />
              );
            })
          ) : (
            shipmentItems.map((item) => (
              <UserActionCard
                actions={[item]}
                key={item.id}
                onAutoConfirmExpired={onAutoConfirmExpired}
                onClick={onClick}
                shipmentAutoConfirmAt={shipmentAutoConfirmAt}
                variant="shipment"
              />
            ))
          )}
        </div>
      )}
    </section>
  );
};

export default UserActionsSection;